import {
  Document,
  DocumentDto,
  DocumentDetailsDto,
  DocumentVersion,
  VersionCompareResult,
  DocumentSharingDto,
  PublicDocumentDetailsDto,
  DocumentLifecycleScheduleDto,
  UpdateDocumentLifecycleSchedulePayload,
} from '../types/documents';
import { api } from './api';
import { DocumentNode } from '../types/documents';
import { SignerInputDto } from './documentSignatureService';

export interface SignerPlacementInput {
  signerKey: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DocumentPlacementInput {
  fileName: string;
  placements: SignerPlacementInput[];
}

/** Opções alinhadas ao `CreateDocumentCommand` (resolução de workflow na criação). */
export interface CreateDocumentWorkflowOptions {
  skipWorkflow?: boolean;
  manualWorkflowId?: string;
  templateId?: string;
  fileExtension?: string;
  requiresSignature?: boolean;
  signers?: SignerInputDto[];
  documentPlacements?: DocumentPlacementInput[];
  signatureExpiresAt?: string | null;
}

/** Nó plano para seleção de documentos (ex.: escopo de agente). */
export interface DocumentPickerNode {
  id: string;
  title: string;
  nodeType: string;
  path: string;
  fullPath: string;
}

class DocumentService {
  /**
   * Tenant (Customer) selecionado por um administrador do cliente proprietário para operar a
   * árvore de documentos de outro cliente. Quando nulo, todas as operações usam o tenant do
   * usuário autenticado. Aplicado apenas às chamadas relacionadas a documentos.
   */
  private activeTenantId: string | null = null;

  setActiveTenantId(tenantId: string | null): void {
    const normalized = tenantId?.trim();
    this.activeTenantId = normalized ? normalized : null;
  }

  getActiveTenantId(): string | null {
    return this.activeTenantId;
  }

  /** Query/body param somente quando o tenant selecionado difere do tenant do token. */
  buildCustomerIdParams(): { customerId?: string } {
    if (!this.activeTenantId) {
      return {};
    }

    const storageCustomerId = this.getStorageValue('@KnowInDoc:customer_id');
    if (storageCustomerId && this.activeTenantId === storageCustomerId) {
      return {};
    }

    return { customerId: this.activeTenantId };
  }

  private getStorageValue(key: string): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  }

  /** Tenant alvo das operações de documento: o selecionado pelo admin ou o do usuário autenticado. */
  private resolveTargetCustomerId(): string | null {
    return this.activeTenantId || this.getStorageValue('@KnowInDoc:customer_id');
  }

  async getDocuments(): Promise<Document[]> {
    const response = await api.get('/documents');
    return response.data;
  }

  async getDocument(id: string): Promise<DocumentDto | undefined> {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  }

  async getDocumentDetails(id: string): Promise<DocumentDetailsDto | undefined> {
    const response = await api.get(`/documents/${id}/details`);
    return response.data;
  }

  async getDocumentVersionHistory(documentId: string): Promise<DocumentVersion[]> {
    const response = await api.get(`/documents/${documentId}/versions`);
    return response.data || [];
  }

  async compareDocumentVersions(documentId: string, versionAId: string, versionBId: string): Promise<VersionCompareResult> {
    const response = await api.get(`/documents/${documentId}/versions/compare`, {
      params: { v1: versionAId, v2: versionBId }
    });
    return response.data;
  }

  async restoreDocumentVersion(documentId: string, versionId: string): Promise<void> {
    await api.post(`/documents/${documentId}/versions/${versionId}/restore`);
  }

  // Função auxiliar para detectar se uma URL é externa (da API) ou local (arquivo selecionado)
  private isExternalUrl(url: string): boolean {
    // URLs locais começam com 'blob:' ou são URLs de objeto criadas pelo URL.createObjectURL
    // URLs externas são URLs HTTP/HTTPS que vêm da API
    if (!url || typeof url !== 'string') return false;
    
    // URLs locais (arquivos selecionados pelo usuário)
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return false;
    }
    
    // URLs externas (da API) - HTTP/HTTPS
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return true;
    }
    
    // URLs relativas ou outros formatos são considerados externos
    return true;
  }

  // Função auxiliar para converter URL externa para blob
  private async urlToBlob(url: string): Promise<Blob> {
    try {
      // Tentar fazer fetch com configurações que podem ajudar com CORS
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors', // Tentar CORS
        credentials: 'omit', // Não enviar cookies
        headers: {
          'Accept': '*/*',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Erro ao converter URL para blob:', error);
      
      // Se falhar por CORS, tentar uma abordagem alternativa
      try {
        // Para URLs do MinIO/S3, tentar adicionar parâmetros que podem ajudar
        if (url.includes('minio') || url.includes('localhost:9000')) {
          // Tentar sem parâmetros de assinatura se possível
          const cleanUrl = url.split('?')[0];
          const response = await fetch(cleanUrl, {
            method: 'GET',
            mode: 'no-cors', // Tentar sem CORS
          });
          
          if (response.type === 'opaque') {
            // Se for opaque, criar um blob vazio e logar o problema
            console.warn('=== DEBUG: URL retornou resposta opaque, criando blob vazio ===');
            return new Blob([''], { type: 'application/octet-stream' });
          }
          
          return await response.blob();
        }
        
        throw error; // Re-throw se não conseguir resolver
      } catch (fallbackError) {
        console.error('=== DEBUG: Abordagem alternativa também falhou ===', fallbackError);
        
        // Como último recurso, criar um blob vazio e logar o problema
        console.warn('=== DEBUG: Criando blob vazio como fallback ===');
        return new Blob([''], { type: 'application/octet-stream' });
      }
    }
  }

  async createDocument(
    document: Omit<Document, 'id' | 'createdAt' | 'updatedAt' | 'version'>,
    path?: string,
    workflow?: CreateDocumentWorkflowOptions
  ): Promise<Document> {
    const customerId = this.resolveTargetCustomerId();
    const userId = this.getStorageValue('@KnowInDoc:user_id');
    
    const formData = new FormData();
    formData.append('title', document.title || '');
    formData.append('description', document.description || '');
    formData.append('content', document.content || '');
    formData.append('path', path || '/'); // Usar o path fornecido ou raiz como fallback
    formData.append('status', document.status || 'edit'); // Adicionar o status do documento
    if (document.publicationExpiresAt !== undefined) {
      formData.append('publicationExpiresAt', document.publicationExpiresAt ?? '');
    }
       
    // Adicionar coverImage como arquivo se existir
    if (document.coverImage) {
      try {
        if (this.isExternalUrl(document.coverImage)) {
          // URL externa (da API) - converter para blob
          const coverBlob = await this.urlToBlob(document.coverImage);
          formData.append('cover', coverBlob, 'cover.jpg');
        } else {
          // URL local (arquivo selecionado pelo usuário) - já é um blob
          const coverBlob = await fetch(document.coverImage).then(r => r.blob());
          formData.append('cover', coverBlob, 'cover.jpg');
        }
      } catch (error) {
        console.error('=== DEBUG: Erro ao processar cover ===', error);
      }
    }
    
    // Adicionar anexos como arquivos se existirem
    if (document.attachments && document.attachments.length > 0) {
      for (const attachment of document.attachments) {
        try {
          if (this.isExternalUrl(attachment.url)) {
            // URL externa (da API) - converter para blob
            const attachmentBlob = await this.urlToBlob(attachment.url);
            formData.append('attachments', attachmentBlob, attachment.name);
          } else {
            // URL local (arquivo selecionado pelo usuário) - já é um blob
            const attachmentBlob = await fetch(attachment.url).then(r => r.blob());
            formData.append('attachments', attachmentBlob, attachment.name);
          }
        } catch (error) {
          console.error('=== DEBUG: Erro ao processar anexo ===', attachment.name, error);
        }
      }
    }
    
    // Adicionar versão atual
    formData.append('currentVersion', '0.1');
    
    if (customerId) formData.append('customerId', customerId);
    if (userId) formData.append('userId', userId);

    if (workflow?.skipWorkflow === true) {
      formData.append('skipWorkflow', 'true');
    }
    if (workflow?.manualWorkflowId) {
      formData.append('manualWorkflowId', workflow.manualWorkflowId);
    }
    if (workflow?.templateId) {
      formData.append('templateId', workflow.templateId);
    }
    if (workflow?.fileExtension) {
      formData.append('fileExtension', workflow.fileExtension);
    }
    if (workflow?.requiresSignature) {
      formData.append('requiresSignature', 'true');
    }
    if (workflow?.signers && workflow.signers.length > 0) {
      formData.append('signers', JSON.stringify(workflow.signers));
    }
    if (workflow?.signatureExpiresAt) {
      formData.append('signatureExpiresAt', workflow.signatureExpiresAt);
    }
    
    const response = await api.post('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  private async buildDocumentFormData(
    id: string,
    document: Partial<Document>
  ): Promise<FormData> {
    const customerId = this.getStorageValue('@KnowInDoc:customer_id');
    const userId = this.getStorageValue('@KnowInDoc:user_id');

    const formData = new FormData();

    formData.append('id', id);

    if (document.title !== undefined)
    {
      formData.append('title', document.title);
    }
    if (document.description !== undefined)
    {
      formData.append('description', document.description);
    }
    if (document.content !== undefined)
    {
      formData.append('content', document.content);
    }
    if (document.publicationExpiresAt !== undefined)
    {
      formData.append('publicationExpiresAt', document.publicationExpiresAt ?? '');
    }

    if (document.coverImage)
    {
      try
      {
        if (this.isExternalUrl(document.coverImage))
        {
          const coverBlob = await this.urlToBlob(document.coverImage);
          formData.append('cover', coverBlob, 'cover.jpg');
        }
        else
        {
          const coverBlob = await fetch(document.coverImage).then(r => r.blob());
          formData.append('cover', coverBlob, 'cover.jpg');
        }
      }
      catch (error)
      {
        console.error('=== DEBUG: Erro ao processar cover ===', error);
      }
    }

    if (document.attachments && document.attachments.length > 0)
    {
      for (const attachment of document.attachments)
      {
        try
        {
          if (this.isExternalUrl(attachment.url))
          {
            const attachmentBlob = await this.urlToBlob(attachment.url);
            formData.append('attachments', attachmentBlob, attachment.name);
          }
          else
          {
            const attachmentBlob = await fetch(attachment.url).then(r => r.blob());
            formData.append('attachments', attachmentBlob, attachment.name);
          }
        }
        catch (error)
        {
          console.error('=== DEBUG: Erro ao processar anexo ===', attachment.name, error);
        }
      }
    }

    formData.append('currentVersion', '0.1');

    if (customerId)
    {
      formData.append('customerId', customerId);
    }
    if (userId)
    {
      formData.append('userId', userId);
    }

    return formData;
  }

  async updateDocument(id: string, document: Partial<Document>): Promise<Document> {
    const formData = await this.buildDocumentFormData(id, document);

    const response = await api.put('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Publica a versão corrente. Não altera título/corpo/capa/anexos —
   * persistir conteúdo antes com updateDocument quando necessário.
   */
  async publishDocument(
    id: string,
    options?: { publicationExpiresAt?: string | null }
  ): Promise<Document> {
    const body =
      options === undefined
        ? {}
        : {
            patchPublicationExpiresAt: true,
            publicationExpiresAt: options.publicationExpiresAt ?? null,
          };

    const response = await api.post(`/documents/${id}/publish`, body);
    return response.data;
  }

  async deleteDocument(id: string): Promise<void> {
    const response = await api.delete<boolean>(`/documents/${id}`);
    if (response.data === false) {
      throw new Error('Não foi possível excluir o documento.');
    }
  }

  async getDocumentsByStatus(
    status: 'edit' | 'review' | 'signature' | 'published' | 'archived'
  ): Promise<Document[]> {
    try {
      const response = await api.get(`/documents/status/${status}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar documentos com status ${status}:`, error);
      throw error;
    }
  }

  async getDocumentsByTag(tag: string): Promise<Document[]> {
    const response = await api.get(`/documents?tag=${encodeURIComponent(tag)}`);
    return response.data;
  }

  async getDocumentsByCategory(categoryId: number): Promise<Document[]> {
    const response = await api.get(`/documents?category=${categoryId}`);
    return response.data;
  }

  async updateDocumentStatus(
    id: string,
    status: 'edit' | 'review' | 'signature' | 'published' | 'archived'
  ): Promise<Document> {
    try {
      const response = await api.patch(`/documents/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar status do documento ${id}:`, error);
      throw error;
    }
  }

  async sendDocumentForReview(id: string): Promise<void> {
    await api.post(`/documents/${id}/versions/send-review`);
  }

  async archiveDocument(id: string): Promise<void> {
    try {
      await api.post(`/documents/${id}/versions/archive`);
    }
    catch (error) {
      console.error(`Erro ao atualizar status do documento ${id}:`, error);
      throw error;
    }
  }

  async approveDocument(id: string): Promise<void> {
    await api.post(`/documents/${id}/versions/approve`);
  }

  async rejectDocument(id: string, notes: string): Promise<void> {
    await api.post(`/documents/${id}/versions/reject`, { notes });
  }


  async getDocumentWithChildren(path: string): Promise<DocumentNode> {
    const params: Record<string, string> = { path };
    if (this.activeTenantId) {
      params.customerId = this.activeTenantId;
    }
    const response = await api.get('/documents/children', { params });
    // Garante que countChild está presente e retorna o documento com filhos
    return { 
      ...response.data, 
      countChild: response.data.countChild ?? 0,
      children: response.data.children || []
    };
  }

  async createFolder(folder: { name: string; path?: string }): Promise<DocumentNode> {
    const customerId = this.resolveTargetCustomerId();
    const userId = this.getStorageValue('@KnowInDoc:user_id');
    
    const formData = new FormData();
    formData.append('name', folder.name);
    if (folder.path) formData.append('path', folder.path);
    if (customerId) formData.append('customerId', customerId);
    if (userId) formData.append('userId', userId);
    
    const response = await api.post('/documents/folder', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteNode(id: string | number): Promise<void> {
    const response = await api.delete<boolean>(`/documents/${id}`);
    if (response.data === false) {
      throw new Error('Não foi possível excluir o documento.');
    }
  }

  async uploadDocuments({
    files,
    path,
    workflow,
  }: {
    files: File[];
    path?: string;
    workflow?: CreateDocumentWorkflowOptions;
  }): Promise<any> {
    const customerId = this.resolveTargetCustomerId();
    const userId = this.getStorageValue('@KnowInDoc:user_id');
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (path) formData.append('path', path);
    if (customerId) formData.append('customerId', customerId);
    if (userId) formData.append('userId', userId);
    if (workflow?.skipWorkflow === true) {
      formData.append('skipWorkflow', 'true');
    }
    if (workflow?.manualWorkflowId) {
      formData.append('manualWorkflowId', workflow.manualWorkflowId);
    }
    if (workflow?.templateId) {
      formData.append('templateId', workflow.templateId);
    }
    if (workflow?.requiresSignature) {
      formData.append('requiresSignature', 'true');
    }
    if (workflow?.signers && workflow.signers.length > 0) {
      formData.append('signers', JSON.stringify(workflow.signers));
    }
    if (workflow?.signatureExpiresAt) {
      formData.append('signatureExpiresAt', workflow.signatureExpiresAt);
    }
    if (workflow?.documentPlacements && workflow.documentPlacements.length > 0) {
      formData.append('documentPlacements', JSON.stringify(workflow.documentPlacements));
    }
    const response = await api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async addCategoryToDocument(documentId: string, categoryId: string): Promise<void> {
    await api.post(`/documents/categories/${documentId}/${categoryId}`);
  }

  async removeCategoryFromDocument(documentId: string, categoryId: string): Promise<void> {
    await api.delete(`/documents/categories/${documentId}/${categoryId}`);
  }

  async addTagToDocument(documentId: string, tagId: string): Promise<void> {
    await api.post(`/documents/tags/${documentId}/${tagId}`);
  }

  async removeTagFromDocument(documentId: string, tagId: string): Promise<void> {
    await api.delete(`/documents/tags/${documentId}/${tagId}`);
  }

  async updateDocumentDescription(documentId: string, description: string): Promise<void> {
    await api.patch(`/documents/${documentId}/description`, { description }, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  async rename(documentId: string, title: string): Promise<DocumentDto> {
    const response = await api.patch(`/documents/${documentId}/rename`, { title }, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  async moveDocument(documentId: string, destinationParentId: string): Promise<DocumentDto> {
    const response = await api.patch(
      `/documents/${documentId}/move`,
      { destinationParentId },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  }

  async duplicateDocument(documentId: string, title?: string): Promise<DocumentDto> {
    const trimmed = title?.trim();
    const body = trimmed ? { title: trimmed } : {};
    const response = await api.post(`/documents/${documentId}/duplicate`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  async getDocumentSharing(documentId: string): Promise<DocumentSharingDto> {
    const response = await api.get(`/documents/${documentId}/sharing`);
    return response.data;
  }

  async updateDocumentSharing(documentId: string, isPublic: boolean): Promise<DocumentSharingDto> {
    const response = await api.patch(
      `/documents/${documentId}/sharing`,
      { isPublic },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  }

  async getPublicDocumentDetails(documentId: string): Promise<PublicDocumentDetailsDto | null> {
    try {
      const response = await api.get<PublicDocumentDetailsDto>(`/public/documents/${documentId}`);
      return response.data;
    } catch (error: unknown) {
      const ax = error as { response?: { status?: number } };
      if (ax.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async searchDocuments(query: string): Promise<DocumentNode[]> {
    const response = await api.get(`/documents/search?q=${encodeURIComponent(query)}`);
    return response.data || [];
  }

  /**
   * Árvore achatada para o seletor de documentos.
   * Com workspaceId, o acervo é o do customer do workspace; sem ele, usa GET /documents/bypath.
   * Pastas entram na travessia mas não na lista; exclui entradas sem id.
   */
  async getDocumentsForPicker(
    startPath = '/',
    nextLimitLevel = 8,
    workspaceId?: string,
  ): Promise<DocumentPickerNode[]> {
    const root = await this.fetchPickerRoot(startPath, nextLimitLevel, workspaceId);
    return this.flattenPickerNodes(root, false);
  }

  /**
   * Pastas achatadas para seleção.
   * Com workspaceId, o acervo é o do customer do workspace; sem ele, usa GET /documents/bypath.
   */
  async getFolderPickerNodes(
    startPath = '/',
    nextLimitLevel = 8,
    workspaceId?: string,
  ): Promise<DocumentPickerNode[]> {
    const root = await this.fetchPickerRoot(startPath, nextLimitLevel, workspaceId);
    return this.flattenPickerNodes(root, true);
  }

  /**
   * Com workspaceId, a árvore é a do customer daquele workspace.
   * Sem workspaceId, permanece o bypath da sessão (árvore de Documentos).
   */
  private async fetchPickerRoot(
    startPath: string,
    nextLimitLevel: number,
    workspaceId?: string,
  ): Promise<Record<string, unknown> | null> {
    const params: Record<string, string | number> = {
      startPath,
      nextLimitLevel,
    };

    if (!workspaceId && this.activeTenantId)
    {
      params.customerId = this.activeTenantId;
    }

    const url = workspaceId
      ? `/workspace-documents/by-workspace/${workspaceId}/picker`
      : '/documents/bypath';

    const response = await api.get(url, { params });
    const root = response.data as Record<string, unknown> | null | undefined;
    if (!root)
    {
      return null;
    }

    return root;
  }

  private flattenPickerNodes(
    root: Record<string, unknown> | null,
    foldersOnly: boolean,
  ): DocumentPickerNode[] {
    if (!root)
    {
      return [];
    }

    const out: DocumentPickerNode[] = [];
    const walk = (n: Record<string, unknown>) => {
      const id = n.id != null ? String(n.id) : '';
      const nt = String(n.nodeType || '').toLowerCase();
      const isFolder = nt === 'folder';
      if (id && isFolder === foldersOnly)
      {
        out.push({
          id,
          title: String(n.title || ''),
          nodeType: String(n.nodeType || ''),
          path: String(n.path || ''),
          fullPath: String(n.fullPath || n.path || ''),
        });
      }
      const children = n.children as Record<string, unknown>[] | undefined;
      if (Array.isArray(children))
      {
        for (const c of children)
        {
          walk(c);
        }
      }
    };
    walk(root);
    return out;
  }

  // Método para avaliar documento
  async evaluateDocument(documentId: string, evaluation: number | null): Promise<void> {
    try {
      await api.post('/documents/evaluations', {
        documentId: documentId,
        evaluation: evaluation
      });
    } catch (error: any) {
      console.error('[documentService.evaluateDocument] Erro na requisição:', error);
      console.error('[documentService.evaluateDocument] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // Método para marcar documento como favorito
  async addToFavorites(documentId: string): Promise<void> {
    try {
      await api.post('/documents/favorites', {
        documentId: documentId
      });
    } catch (error: any) {
      console.error('[documentService.addToFavorites] Erro na requisição:', error);
      console.error('[documentService.addToFavorites] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // Método para remover documento dos favoritos
  async removeFromFavorites(documentId: string): Promise<void> {
    try {
      await api.delete(`/documents/favorites?documentId=${documentId}`);
    } catch (error: any) {
      console.error('[documentService.removeFromFavorites] Erro na requisição:', error);
      console.error('[documentService.removeFromFavorites] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // Método para buscar documentos favoritos do usuário
  async getFavoriteDocuments(): Promise<DocumentNode[]> {
    try {
      const response = await api.get('/documents/favorites');
      return response.data || [];
    } catch (error: any) {
      console.error('[documentService.getFavoriteDocuments] Erro na requisição:', error);
      console.error('[documentService.getFavoriteDocuments] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async updateDocumentLifecycleSchedule(
    documentId: string,
    body: UpdateDocumentLifecycleSchedulePayload
  ): Promise<DocumentLifecycleScheduleDto> {
    const response = await api.patch(`/documents/${documentId}/lifecycle-schedule`, body);
    return response.data;
  }

  async archivePublishedOutsideWorkflow(documentId: string): Promise<DocumentDto> {
    const response = await api.post(`/documents/${documentId}/archive-without-workflow`);
    return response.data;
  }
}

export const documentService = new DocumentService(); 
